/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import type { UnsafeTileInnerProps, UnsafeTileProps } from './types';

/**
 * IMPORTANT CONTEXT:
 * This is an ADS sanctioned, but ultimately unsafe component that was created to match the Ribbon design.
 * It contains overrides that break NavX paradigms and should not be used outside of the Ribbon until such a
 * time as NavX paradigms can be updated to match Ribbon design needs. If you are using this component please
 * ensure you have read and understand the ADS approval for this component and are following all guidelines
 * outlined within it.
 *
 * See https://hello.atlassian.net/wiki/spaces/DST/pages/6395990823/DACI+Interactive+app+logos+in+ribbon
 *
 * One of the key objectives of this component is to ensure that the "notch" color matches the color of the
 * selected Logo. In order to achieve this the "notch" is actually a duplicate of the main logo with overrides
 * to make the icon color transparent and the tile color match the logo color. This means that any changes to
 * the way logos are rendered in NavX could potentially cause regressions in the appearance of the notch.
 *
 * Because of this, there is a VR test that renders all states of the tile with the current overrides in place.
 */

const styles = cssMap({
	root: {
		width: 32,
		height: 32,
		position: 'relative',
		borderRadius: token('radius.tile'),
	},
	focused: {
		boxShadow: `0 0 0 ${token('border.width.focused')} ${token('color.border.focused')}`,
	},
	focusedSelected: {
		outlineStyle: 'solid',
		outlineWidth: token('border.width.focused'),
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
	},
	rootSmall: {
		width: 24,
		height: 24,
	},
	hovered: {
		'&::before': {
			backgroundColor: token('color.interaction.hovered'),
		},
	},
	interactive: {
		cursor: 'pointer',

		'&::before': {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
			borderRadius: token('radius.tile'),
		},
		'&:hover': {
			'&::before': {
				backgroundColor: token('color.interaction.hovered'),
			},
		},
		'&:active': {
			'&::before': {
				backgroundColor: token('color.interaction.pressed'),
			},
		},
	},
	unselected: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			'--tile-color': 'transparent',
			'--icon-color': token('color.icon'),
		},
	},
	disabled: {
		opacity: token('opacity.disabled'),
	},
	draggable: {
		cursor: 'grab',
		'&:active': {
			cursor: 'grabbing',
		},
	},
	editMode: {
		outlineStyle: 'dashed',
		outlineWidth: token('border.width'),
		outlineColor: token('color.border'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		outlineOffset: -1,
		'&::before': {
			content: 'unset',
		},
	},
	editModeInteractive: {
		'&:hover': {
			backgroundColor: token('color.background.selected'),
			outlineColor: token('color.border.bold'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
			outlineColor: token('color.border.selected'),
		},
	},
	editModeHovered: {
		backgroundColor: token('color.background.selected'),
		outlineColor: token('color.border.bold'),
	},
	editModeFocused: {
		boxShadow: 'none',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			'--tile-color': 'transparent',
			'--icon-color': token('color.icon'),
		},

		'&::before': {
			content: '""',
			position: 'absolute',
			top: -2,
			left: -2,
			width: 'calc(100% + 4px)',
			height: 'calc(100% + 4px)',
			borderRadius: token('radius.tile'),
			boxShadow: `0 0 0 ${token('border.width.focused')} ${token('color.border.focused')}`,
		},
	},
	editModeFocusedSelected: {
		boxShadow: token('elevation.shadow.overlay'),
		outlineStyle: 'solid',
	},
	notch: {
		position: 'relative', // This is required to ensure the notch appears relative to the frame it's placed in
		left: -16, // A value of -16 ensures we're taking a MIDDLE slice of the Logo so borders won't bleed in
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			'--icon-color': 'transparent', // This is important to ensure the logo itself is invisible and only the background color shows
		},
	},
	notchFrame: {
		position: 'absolute',
		left: 0, // This ensures the notch frame is aligned to the left edge of the exterior container
		width: `4px`, // This should map to the desired notch width which is 1/8 of the tile width (32px)
		overflow: 'hidden',
		height: `24px`, // This should map to the desired notch height which is 3/4 of the tile height (32px)
		top: token('space.050'), // This should map to the desired notch vertical position which is 1/8 of the tile height (32px)
		borderTopRightRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
	},
	// This exterior container is required to ensure that the notch appears within the tile boundary
	exterior: {
		position: 'relative', // This ensures the notch frame is positioned relative to the tile and not the entire page
		width: '100%',
		height: 32,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBottom: token('space.075'), // Matches designs for space between Logo and Text
	},
});

export function UnsafeTileInner(props: UnsafeTileInnerProps): JSX.Element {
	const {
		isDisabled,
		isDragPreview,
		isEditMode,
		isFocused,
		isHovered,
		isNotch,
		isSelected,
		logo: Logo,
		size,
	} = props;
	return (
		<div
			data-app-tile-logo={!isNotch ? 'true' : undefined}
			css={[
				styles.root,
				!isNotch && isFocused && !isSelected && styles.focused,
				!isNotch && isFocused && isSelected && styles.focusedSelected,
				!isDisabled && styles.interactive,
				!isDisabled && !isEditMode && isHovered && styles.hovered,
				!isDisabled && styles.draggable,
				isDisabled && styles.disabled,
				!isSelected && !isDragPreview && styles.unselected,
				isEditMode && styles.editMode,
				isEditMode && !isDisabled && styles.editModeInteractive,
				isEditMode && !isDisabled && isHovered && styles.editModeHovered,
				isEditMode && isFocused && styles.editModeFocused,
				isEditMode && isFocused && isSelected && styles.editModeFocusedSelected,
				isNotch && styles.notch,
				size === 'small' && styles.rootSmall,
			]}
		>
			<Logo
				// set label to empty string to avoid duplicating the label
				label={''}
				shouldUseNewLogoDesign
				appearance={isDragPreview ? 'inverse' : 'brand'}
				size={size || 'medium'}
			/>
		</div>
	);
}

export function UnsafeTile(props: UnsafeTileProps): JSX.Element {
	const { isSelected, hideNotch, isEditMode } = props;
	if (!isSelected || isEditMode) {
		return (
			<div css={styles.exterior}>
				<UnsafeTileInner {...props} />
			</div>
		);
	}

	return (
		<div css={styles.exterior}>
			{!hideNotch && (
				<div css={styles.notchFrame}>
					<UnsafeTileInner {...props} isNotch />
				</div>
			)}
			<UnsafeTileInner {...props} isNotch={false} />
		</div>
	);
}
